import type { GuidelineResponse, UnfixableDomainRule } from '../types/guideline';
import type { GuidelineDraftContent, GuidelineDraftMetadata } from '../types/accuracy';

export const DEFAULT_CHUNK_SETTINGS = {
  chunk_context_before: 5000,
  chunk_context_after: 5000,
  max_chunk_size: 10000,
  chunk_overlap: 500,
  keyword_group_distance: 5000,
};

export function emptyDraftMetadata(): GuidelineDraftMetadata {
  return {
    related_asps: [],
    operation_companies: [],
    media_names: [],
    chunk_settings: { ...DEFAULT_CHUNK_SETTINGS },
    keyword_list: [],
    enable_search: false,
    search_keywords: [],
    enable_filtering: true,
    other_content_keywords: [],
    auto_expand_keywords: [],
    enable_unfixable_classification: false,
    unfixable_by_domain: [],
  };
}

export function guidelineResponseToDraftMetadata(guideline: GuidelineResponse): GuidelineDraftMetadata {
  return {
    related_asps: guideline.aspOptions ?? [],
    operation_companies: guideline.operationCompanyOptions ?? [],
    media_names: guideline.mediaOptions ?? [],
    chunk_settings: {
      chunk_context_before: guideline.chunkContextBefore ?? DEFAULT_CHUNK_SETTINGS.chunk_context_before,
      chunk_context_after: guideline.chunkContextAfter ?? DEFAULT_CHUNK_SETTINGS.chunk_context_after,
      max_chunk_size: guideline.maxChunkSize ?? DEFAULT_CHUNK_SETTINGS.max_chunk_size,
      chunk_overlap: guideline.chunkOverlap ?? DEFAULT_CHUNK_SETTINGS.chunk_overlap,
      keyword_group_distance:
        guideline.keywordGroupDistance ?? DEFAULT_CHUNK_SETTINGS.keyword_group_distance,
    },
    keyword_list: guideline.keywordList ?? [],
    enable_search: guideline.enableSearch ?? false,
    search_keywords: guideline.searchKeywords ?? [],
    enable_filtering: guideline.enableFiltering ?? true,
    other_content_keywords: guideline.titleExtractionKeywords ?? [],
    auto_expand_keywords: guideline.autoExpandKeywords ?? [],
    enable_unfixable_classification: guideline.enableUnfixableClassification ?? false,
    unfixable_by_domain: guideline.unfixableByDomain ?? [],
  };
}

export function normalizeDraftContent(content: GuidelineDraftContent): GuidelineDraftContent {
  const wire = content as GuidelineDraftContent & {
    version?: {
      violation_categories?: GuidelineDraftContent['violation_categories'];
      related_asps?: string[];
      operation_companies?: string[];
      media_names?: string[];
      chunk_context_before?: number;
      chunk_context_after?: number;
      max_chunk_size?: number;
      chunk_overlap?: number;
      keyword_group_distance?: number;
      keyword_list?: string[];
      enable_search?: boolean;
      search_keywords?: string[];
      enable_filtering?: boolean;
      other_content_keywords?: string[];
      auto_expand_keywords?: string[];
      enable_unfixable_classification?: boolean;
      unfixable_by_domain?: UnfixableDomainRule[];
    };
  };

  if (wire.version) {
    const v = wire.version;
    content = {
      guideline_id: wire.guideline_id,
      base_version: wire.base_version,
      metadata: {
        related_asps: v.related_asps ?? [],
        operation_companies: v.operation_companies ?? [],
        media_names: v.media_names ?? [],
        chunk_settings: {
          chunk_context_before: v.chunk_context_before ?? DEFAULT_CHUNK_SETTINGS.chunk_context_before,
          chunk_context_after: v.chunk_context_after ?? DEFAULT_CHUNK_SETTINGS.chunk_context_after,
          max_chunk_size: v.max_chunk_size ?? DEFAULT_CHUNK_SETTINGS.max_chunk_size,
          chunk_overlap: v.chunk_overlap ?? DEFAULT_CHUNK_SETTINGS.chunk_overlap,
          keyword_group_distance:
            v.keyword_group_distance ?? DEFAULT_CHUNK_SETTINGS.keyword_group_distance,
        },
        keyword_list: v.keyword_list ?? [],
        enable_search: v.enable_search ?? false,
        search_keywords: v.search_keywords ?? [],
        enable_filtering: v.enable_filtering ?? true,
        other_content_keywords: v.other_content_keywords ?? [],
        auto_expand_keywords: v.auto_expand_keywords ?? [],
        enable_unfixable_classification: v.enable_unfixable_classification ?? false,
        unfixable_by_domain: v.unfixable_by_domain ?? [],
      },
      violation_categories: v.violation_categories ?? [],
    };
  }

  const items =
    content.violation_categories ??
    ((content as { items?: GuidelineDraftContent['violation_categories'] }).items || []);
  return {
    guideline_id: content.guideline_id,
    base_version: content.base_version,
    metadata: content.metadata ?? emptyDraftMetadata(),
    violation_categories: items.map((cat) => ({
      id: cat.id,
      violation_category: cat.violation_category,
      rules: (cat.rules || []).map((rule, idx) => ({
        ...rule,
        id: rule.id || `${cat.id}-rule-${idx + 1}`,
        type: rule.type || inferRuleType(rule),
      })),
    })),
  };
}

/** True when draft has at least one category (handles API `version` wire format). */
export function draftHasCategories(content: GuidelineDraftContent | null | undefined): boolean {
  if (!content) return false;
  return normalizeDraftContent(content).violation_categories.length > 0;
}

function inferRuleType(rule: { type?: string; violation_check_prompt?: string | null; existence_check_prompt?: string | null; forbidden_regular_expressions?: string[] | null; musthave_regular_expressions?: string[] | null; forbidden_image_assets?: unknown[]; required_image_assets?: unknown[] }): string {
  if (rule.type && rule.type !== 'description') return rule.type;
  if (rule.violation_check_prompt || rule.existence_check_prompt) return 'text_check';
  if (rule.forbidden_regular_expressions?.length) return 'forbidden_regular_expression_check';
  if (rule.musthave_regular_expressions?.length) return 'musthave_regular_expression_check';
  if (rule.forbidden_image_assets?.length) return 'forbidden_image';
  if (rule.required_image_assets?.length) return 'required_image_match';
  return 'text_check';
}

export function listToMultiline(list: string[] | undefined): string {
  return list && list.length > 0 ? list.join('\n') : '';
}

export function parseMultilineInput(text: string): string[] {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

export function sortUnfixableRules(rules: UnfixableDomainRule[]): UnfixableDomainRule[] {
  return [...rules].sort((a, b) => (a.source_row ?? 0) - (b.source_row ?? 0));
}
